import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  HeartHandshake, UserPlus, Trash2, Copy, Check, Loader2,
  AlertCircle, MessageSquare, Link2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function AlliesDialog() {
  const [open, setOpen] = useState(false);
  const [allies, setAllies] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [notice, setNotice] = useState('');

  const inviteLink = useCallback(function (t) {
    return window.location.origin + '/ally-invite/' + t;
  }, []);

  const load = useCallback(async function () {
    setLoading(true);
    try {
      const [aRes, nRes] = await Promise.all([
        fetch(API_URL + '/api/leader/allies', { credentials: 'include' }),
        fetch(API_URL + '/api/leader/notes', { credentials: 'include' })
      ]);
      if (aRes.ok) setAllies(await aRes.json());
      if (nRes.ok) setNotes(await nRes.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(function () {
    if (open) load();
  }, [open, load]);

  async function handleInvite(e) {
    e.preventDefault();
    setError('');
    setNotice('');
    if (!email.trim()) return;
    setInviting(true);
    try {
      const res = await fetch(API_URL + '/api/leader/allies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim() })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not send invite');
      }
      const invited = email.trim();
      setEmail('');
      if (data.email_sent) {
        setNotice('Invitation email sent to ' + invited + '.');
      } else {
        setNotice('Ally added, but the email could not be sent. Copy the invite link below and share it directly.');
      }
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setInviting(false);
    }
  }

  async function handleRevoke(id) {
    try {
      await fetch(API_URL + '/api/leader/allies/' + id, { method: 'DELETE', credentials: 'include' });
      await load();
    } catch (e) {
      console.error(e);
    }
  }

  function handleCopy(text, key) {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(function () { setCopied(''); }, 1500);
  }

  function formatDate(iso) {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
    catch (e) { return iso; }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2" title="Accountability allies" data-testid="open-allies-button">
          <HeartHandshake className="h-4 w-4" />
          <span className="hidden xl:inline">Allies</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="allies-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif">
            <HeartHandshake className="h-5 w-5 text-primary" /> Accountability Allies
          </DialogTitle>
          <DialogDescription>
            Invite up to 3 people to check in on your progress. They can view your workbook and leave notes — they can't edit it.
          </DialogDescription>
        </DialogHeader>

        {/* Invite form */}
        <form onSubmit={handleInvite} className="space-y-3">
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2 text-sm text-destructive" data-testid="allies-error">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {notice && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-sm text-emerald-700" data-testid="allies-notice">
              <Check className="h-4 w-4 shrink-0" />
              <span>{notice}</span>
            </div>
          )}
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="ally@example.com"
              value={email}
              onChange={function (e) { setEmail(e.target.value); }}
              data-testid="ally-invite-email"
            />
            <Button type="submit" disabled={inviting || allies.length >= 3} data-testid="send-invite-button">
              {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              <span className="ml-2 hidden sm:inline">Invite</span>
            </Button>
          </div>
          {allies.length >= 3 && (
            <p className="text-xs text-muted-foreground">You've reached the maximum of 3 allies. Remove one to add another.</p>
          )}
        </form>

        {/* Allies list */}
        <div>
          <h4 className="text-sm font-semibold mb-2">Your Allies</h4>
          {loading ? (
            <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>
          ) : allies.length === 0 ? (
            <p className="text-sm text-muted-foreground">No allies yet. Invite someone above.</p>
          ) : (
            <div className="space-y-2">
              {allies.map(function (a) {
                return (
                  <div key={a.id} className="border border-border rounded-lg p-3" data-testid={'ally-row-' + a.id}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{a.ally_email}</p>
                        <p className="text-xs text-muted-foreground">Invited {formatDate(a.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {a.status === 'accepted' ? (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-transparent">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Pending</Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={function () { handleRevoke(a.id); }}
                          data-testid={'revoke-ally-' + a.id}
                          title="Remove ally"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {a.status === 'pending' && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 flex items-center gap-2 bg-muted rounded px-2 py-1 text-xs text-muted-foreground truncate">
                          <Link2 className="h-3 w-3 shrink-0" />
                          <span className="truncate">{inviteLink(a.token)}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={function () { handleCopy(inviteLink(a.token), a.id); }}
                          data-testid={'copy-invite-' + a.id}
                        >
                          {copied === a.id ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {allies.some(function (a) { return a.status === 'pending'; }) && (
            <p className="text-xs text-muted-foreground mt-2">
              We email each ally an invite link automatically. You can also copy the link above and send it yourself as a backup.
            </p>
          )}
        </div>

        {/* Notes from allies */}
        <div>
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <MessageSquare className="h-4 w-4" /> Notes from your allies
          </h4>
          {notes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No check-in notes yet.</p>
          ) : (
            <div className="space-y-2">
              {notes.map(function (n) {
                return (
                  <div key={n.id} className="bg-muted/40 border border-border rounded-lg p-3" data-testid={'leader-note-' + n.id}>
                    <p className="text-sm whitespace-pre-wrap">{n.text}</p>
                    <p className="text-xs text-muted-foreground mt-1">{n.ally_email} · {formatDate(n.created_at)}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { AlliesDialog };
